import { FC, useId } from "react"

import {
  Avatar,
  Box,
  Button,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"

import {
  Notice,
  Price,
  TableScrollRegion,
} from "../SendStoreChargeDialog.parts"
import { BalanceCheck, formatStudentName } from "../SendStoreChargeDialog.utils"
import { reviewTableSx } from "./ReviewStep.styles"

interface IProps {
  balanceCheck: BalanceCheck | null
  isRosterPending: boolean
  isRosterFailed: boolean
  onRetryRoster: () => void
  noneCanAfford: boolean
  predictedSkippedCount: number
  costPerStudent: number
  // Charge's aria-describedby points at these while they block it.
  balanceMessageId: string
  rosterErrorId: string
}

const ReviewBalanceCheck: FC<IProps> = ({
  balanceCheck,
  isRosterPending,
  isRosterFailed,
  onRetryRoster,
  noneCanAfford,
  predictedSkippedCount,
  costPerStudent,
  balanceMessageId,
  rosterErrorId,
}) => {
  const { t } = useTranslation()
  const balanceHeadingId = useId()

  if (!balanceCheck && !isRosterPending && !isRosterFailed) return null

  return (
    <Stack gap={1.5}>
      <Typography
        id={balanceHeadingId}
        variant="body1"
        fontWeight={700}
        color="mockup.neutral10"
      >
        {t("storeCharge.balance.heading")}
      </Typography>
      {noneCanAfford ? (
        <Notice id={balanceMessageId} tone="error">
          <Typography variant="body3" color="mockup.error30">
            {t("storeCharge.balance.noneCanAfford")}
          </Typography>
        </Notice>
      ) : (
        predictedSkippedCount > 0 && (
          <Notice tone="warning">
            <Typography variant="body3" color="mockup.neutral20">
              {t("storeCharge.balance.skippedMessage", {
                count: predictedSkippedCount,
              })}
            </Typography>
          </Notice>
        )
      )}
      {isRosterFailed ? (
        <Stack alignItems="flex-start" gap={1}>
          <Typography id={rosterErrorId} variant="body3" color="mockup.error30">
            {t("storeCharge.review.rosterError")}
          </Typography>
          <Button variant="outlined" onClick={onRetryRoster}>
            {t("storeCharge.review.tryAgain")}
          </Button>
        </Stack>
      ) : (
        <TableScrollRegion labelledBy={balanceHeadingId}>
          <Table
            size="small"
            sx={reviewTableSx}
            aria-labelledby={balanceHeadingId}
            aria-busy={isRosterPending || undefined}
          >
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">
                  {t("storeCharge.balance.colStudent")}
                </TableCell>
                <TableCell component="th" scope="col">
                  {t("storeCharge.balance.colBalance")}
                </TableCell>
                <TableCell component="th" scope="col">
                  {t("storeCharge.balance.colCharge")}
                </TableCell>
                <TableCell component="th" scope="col">
                  {t("storeCharge.balance.colBalanceAfter")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isRosterPending &&
                [0, 1, 2].map((row) => (
                  <TableRow key={row} aria-hidden>
                    {[0, 1, 2, 3].map((cell) => (
                      <TableCell key={cell}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {balanceCheck?.rows.map(
                ({ student, skipped, balance, balanceAfter }) => (
                  <TableRow
                    key={student.id}
                    sx={
                      skipped
                        ? {
                            bgcolor: "mockup.secondary90",
                            "& .MuiTableCell-root": {
                              color: "mockup.neutral20",
                            },
                          }
                        : undefined
                    }
                  >
                    <TableCell component="th" scope="row">
                      <Stack
                        direction="row"
                        gap={1}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Avatar
                          src={student.avatar_url}
                          alt=""
                          sx={{
                            width: "28px",
                            height: "28px",
                            color: "common.white",
                          }}
                        />
                        {formatStudentName(student)}
                        {skipped && (
                          <Box
                            component="span"
                            px={1}
                            sx={(theme) => ({
                              border: `1px solid ${theme.palette.mockup.secondary60}`,
                              borderRadius: "8px",
                              typography: "body3",
                            })}
                          >
                            {t("storeCharge.balance.wontBeCharged")}
                          </Box>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack alignItems="flex-start">
                        <Price amount={balance} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {skipped ? (
                        <>
                          <span aria-hidden>—</span>
                          <Box component="span" sx={visuallyHiddenSx}>
                            {t("storeCharge.balance.notChargedA11y")}
                          </Box>
                        </>
                      ) : (
                        <Stack alignItems="flex-start">
                          <Price amount={costPerStudent} />
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack alignItems="flex-start">
                        <Price amount={balanceAfter} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableScrollRegion>
      )}
    </Stack>
  )
}

export default ReviewBalanceCheck
